import type { ExitType } from '@/lib/exitTypes'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  createCompany,
  getCompanies,
  getCompanyById,
  getCompanyDashboard,
  updateCompany,
  deleteCompany,
  getMyCompanies,
  getCompanyMembers,
  getMembersWorkspace,
  addCompanyMember,
  updateCompanyMember,
  removeCompanyMember,
  type GetCompaniesParams,
  type CompanyMembersListParams,
  type MembersWorkspaceQuery,
} from '@/api/services/companies.service'
import { getErrorMessage } from '@/lib/getErrorMessage'
import { dashboardKeys } from '@/features/dashboard/hooks/useDashboardStats'
import type {
  CreateCompanyPayload,
  UpdateCompanyPayload,
  CompanyMember,
  AddCompanyMemberPayload,
  UpdateCompanyMemberPayload,
} from '@/types/company.types'
import type { PaginatedResponse } from '@/types/common.types'

export const companiesKeys = {
  all: ['companies'] as const,
  list: (params: GetCompaniesParams | undefined) => ['companies', 'list', params] as const,
  detail: (id: string) => ['companies', 'detail', id] as const,
  dashboard: (id: string) => ['companies', 'detail', id, 'dashboard'] as const,
  my: ['companies', 'me'] as const,
  members: (companyId: string, params?: CompanyMembersListParams) =>
    ['companies', companyId, 'members', params] as const,
  membersWorkspace: (params: MembersWorkspaceQuery | undefined) =>
    ['companies', 'members', 'workspace', params] as const,
}

function getCompanyMemberUserId(member: CompanyMember): string {
  return typeof member.userId === 'string' ? member.userId : member.userId._id
}

function replaceMemberInList(
  members: CompanyMember[],
  nextMember: CompanyMember,
  companyId: string,
  userId: string
): CompanyMember[] {
  return members.map((member) => {
    const memberCompanyId = member.companyId
    const memberUserId = getCompanyMemberUserId(member)
    return memberCompanyId === companyId && memberUserId === userId ? nextMember : member
  })
}

function updateCachedMember(
  queryClient: ReturnType<typeof useQueryClient>,
  nextMember: CompanyMember,
  companyId: string,
  userId: string
): void {
  queryClient.setQueriesData<PaginatedResponse<CompanyMember>>(
    { queryKey: ['companies', companyId, 'members'] },
    (existing) =>
      existing
        ? {
            ...existing,
            data: replaceMemberInList(existing.data, nextMember, companyId, userId),
          }
        : existing
  )

  queryClient.setQueriesData<PaginatedResponse<CompanyMember>>(
    { queryKey: ['companies', 'members', 'workspace'] },
    (existing) =>
      existing
        ? {
            ...existing,
            data: replaceMemberInList(existing.data, nextMember, companyId, userId),
          }
        : existing
  )
}

export function useMembersWorkspace(
  params: MembersWorkspaceQuery | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: companiesKeys.membersWorkspace(params),
    queryFn: () => getMembersWorkspace(params),
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: options?.enabled !== false,
  })
}

export function useCompanies(
  params?: GetCompaniesParams,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: companiesKeys.list(params),
    queryFn: () => getCompanies(params),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: options?.enabled !== false,
  })
}

export function useCompany(id: string | null) {
  return useQuery({
    queryKey: companiesKeys.detail(id ?? ''),
    queryFn: () => getCompanyById(id!),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useCompanyDashboard(id: string | null) {
  return useQuery({
    queryKey: companiesKeys.dashboard(id ?? ''),
    queryFn: () => getCompanyDashboard(id!),
    enabled: !!id,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    // "Today" and "clocked in now" go stale on their own; keep them live.
    refetchInterval: 60_000,
  })
}

export function useMyCompanies(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: companiesKeys.my,
    queryFn: getMyCompanies,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: options?.enabled !== false,
  })
}

export function useCompanyMembers(
  companyId: string | null,
  params: CompanyMembersListParams = { page: 1, limit: 10 },
) {
  return useQuery({
    queryKey: companiesKeys.members(companyId ?? '', params),
    queryFn: () => getCompanyMembers(companyId!, params),
    enabled: !!companyId,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useCreateCompany() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateCompanyPayload) => createCompany(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companiesKeys.all })
      queryClient.invalidateQueries({ queryKey: companiesKeys.my })
      queryClient.invalidateQueries({ queryKey: dashboardKeys.organization })
      toast.success('Company created')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useUpdateCompany() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCompanyPayload }) =>
      updateCompany(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: companiesKeys.all })
      queryClient.invalidateQueries({ queryKey: companiesKeys.detail(variables.id) })
      toast.success('Company updated')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useDeleteCompany() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteCompany(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companiesKeys.all })
      queryClient.invalidateQueries({ queryKey: companiesKeys.my })
      queryClient.invalidateQueries({ queryKey: dashboardKeys.organization })
      toast.success('Company deleted')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useAddCompanyMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      companyId,
      payload,
    }: {
      companyId: string
      payload: AddCompanyMemberPayload
    }) => addCompanyMember(companyId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: companiesKeys.members(variables.companyId),
      })
      queryClient.invalidateQueries({ queryKey: ['companies', 'members', 'workspace'] })
      queryClient.invalidateQueries({ queryKey: companiesKeys.detail(variables.companyId) })
      queryClient.invalidateQueries({ queryKey: ['shifts-roster'] })
      toast.success('Member added')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useUpdateCompanyMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      companyId,
      userId,
      payload,
    }: {
      companyId: string
      userId: string
      payload: UpdateCompanyMemberPayload
    }) => updateCompanyMember(companyId, userId, payload),
    onSuccess: (member, variables) => {
      updateCachedMember(queryClient, member, variables.companyId, variables.userId)
      queryClient.invalidateQueries({
        queryKey: companiesKeys.members(variables.companyId),
      })
      queryClient.invalidateQueries({ queryKey: ['companies', 'members', 'workspace'] })
      queryClient.invalidateQueries({ queryKey: ['shifts-roster'] })
      // The HR directory shows membership status; the employment stays open.
      queryClient.invalidateQueries({ queryKey: ['hr-employments'] })
      queryClient.invalidateQueries({ queryKey: ['hr-employee-profile', variables.companyId] })
      toast.success('Member updated')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useRemoveCompanyMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      companyId,
      userId,
      exitType,
      reason,
    }: {
      companyId: string
      userId: string
      exitType: ExitType
      reason?: string
    }) => removeCompanyMember(companyId, userId, { exitType, ...(reason && { reason }) }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: companiesKeys.members(variables.companyId),
      })
      queryClient.invalidateQueries({ queryKey: ['companies', 'members', 'workspace'] })
      queryClient.invalidateQueries({ queryKey: companiesKeys.detail(variables.companyId) })
      queryClient.invalidateQueries({ queryKey: ['companies', 'members', 'workspace'] })
      queryClient.invalidateQueries({ queryKey: ['shifts-roster'] })
      // Removing a member ends the People & HR employment there.
      queryClient.invalidateQueries({ queryKey: ['hr-employments'] })
      queryClient.invalidateQueries({ queryKey: ['hr-employee-profile', variables.companyId] })
      toast.success('Member removed')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}
